import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { 
  BookOpen, Clock, Users, Layers, Image as ImageIcon, 
  Building2, FlaskConical, Sparkles, Camera, Info
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import api from '../api/client';
import { toast } from 'sonner';
import { formatEditedShort } from '../utils/formatEditedDate';

// Optimize image URLs for web display (resize Unsplash images to card size)
const optimizeImageUrl = (url) => {
  if (!url) return url;
  if (url.includes('unsplash.com')) {
    const base = url.split('?')[0];
    return `${base}?w=600&h=340&fit=crop&q=75&auto=format`;
  }
  return url;
};

// Domain/Category to gradient and icon mapping
const domainStyles = {
  // Business & Finance
  'finance': { gradient: 'from-emerald-500 to-teal-600', icon: '💰' },
  'business': { gradient: 'from-blue-500 to-indigo-600', icon: '📊' },
  'marketing': { gradient: 'from-pink-500 to-rose-600', icon: '📣' },
  'sales': { gradient: 'from-orange-500 to-amber-600', icon: '🎯' },
  'economics': { gradient: 'from-green-500 to-emerald-600', icon: '📈' },
  
  // Technology
  'technology': { gradient: 'from-violet-500 to-purple-600', icon: '💻' },
  'ai': { gradient: 'from-purple-500 to-indigo-600', icon: '🤖' },
  'genai': { gradient: 'from-indigo-500 to-purple-600', icon: '✨' },
  'data': { gradient: 'from-cyan-500 to-blue-600', icon: '📊' },
  'programming': { gradient: 'from-gray-600 to-slate-700', icon: '👨‍💻' },
  'cloud': { gradient: 'from-sky-500 to-blue-600', icon: '☁️' },
  'ml': { gradient: 'from-blue-500 to-purple-600', icon: '🧠' },
  
  // Science & Healthcare
  'healthcare': { gradient: 'from-red-500 to-rose-600', icon: '🏥' },
  'science': { gradient: 'from-teal-500 to-cyan-600', icon: '🔬' },
  'medical': { gradient: 'from-red-500 to-pink-600', icon: '⚕️' },
  'pharma': { gradient: 'from-green-500 to-teal-600', icon: '💊' },
  
  // Industry
  'manufacturing': { gradient: 'from-slate-500 to-gray-600', icon: '🏭' },
  'retail': { gradient: 'from-amber-500 to-orange-600', icon: '🛒' },
  'supply chain': { gradient: 'from-indigo-500 to-blue-600', icon: '📦' },
  'logistics': { gradient: 'from-blue-500 to-indigo-600', icon: '🚚' },
  'energy': { gradient: 'from-yellow-500 to-orange-600', icon: '⚡' },
  
  // Education & HR
  'education': { gradient: 'from-blue-500 to-cyan-600', icon: '🎓' },
  'hr': { gradient: 'from-purple-500 to-pink-600', icon: '👥' },
  'leadership': { gradient: 'from-amber-500 to-yellow-600', icon: '👔' },
  
  // Default
  'default': { gradient: 'from-slate-500 to-gray-600', icon: '📚' }
};

// Get style based on domain/title keywords
const getStyleForContent = (domain, title, type = 'course') => {
  const searchText = `${domain || ''} ${title || ''}`.toLowerCase();
  
  for (const [key, style] of Object.entries(domainStyles)) {
    if (searchText.includes(key)) {
      return style;
    }
  }
  
  // Type-specific defaults
  if (type === 'lab') return { gradient: 'from-purple-500 to-indigo-600', icon: '🧪' };
  if (type === 'industry-lab') return { gradient: 'from-blue-600 to-slate-700', icon: '🏢' };
  
  return domainStyles.default;
};

/**
 * ImageCard - A visually rich card component for Courses and Labs
 * Features: 
 * - Thumbnail image with 16:9 aspect ratio
 * - Image cropping dialog for uploads
 * - Full description with tooltip for overflow
 * - Upload button only on gradient placeholder (not on custom images)
 * - Optional short title overlay on thumbnail
 */
const ImageCard = ({
  item,
  type = 'course', // 'course', 'lab', 'industry-lab'
  isSelected = false,
  onClick,
  onDoubleClick,
  onImageUpload,
  showUploadButton = false,
  orgId,
  className = '',
  testId
}) => {
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // Cropping state
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: 16 / 9 });
  const [completedCrop, setCompletedCrop] = useState(null);
  
  const style = getStyleForContent(item.domain, item.title, type);
  const hasCustomImage = item.thumbnailUrl && !imageError;
  
  // Short title overlay settings from item
  const showShortTitleOverlay = item.showShortTitleOverlay && item.shortTitle;
  const shortTitle = item.shortTitle || '';
  
  // Get status badge info
  const getStatusBadge = () => {
    if (type === 'course') {
      // PRD: Course Merger -- prefer lifecycleStage when available so
      // Drafts/Frameworks/Populated/Published/Archived all show distinct pills.
      const stageLabels = {
        definition: { label: 'Draft', color: 'bg-slate-500' },
        framework: { label: 'Framework', color: 'bg-violet-500' },
        populated: { label: 'Populated', color: 'bg-amber-500' },
        published: { label: 'Published', color: 'bg-green-500' },
        archived: { label: 'Archived', color: 'bg-zinc-500' },
      };
      if (item.lifecycleStage && stageLabels[item.lifecycleStage]) {
        return stageLabels[item.lifecycleStage];
      }
      // Legacy fallback (pre-backfill rows).
      if (item.isPublished) return { label: 'Published', color: 'bg-green-500' };
      return { label: 'Draft', color: 'bg-gray-500' };
    }
    if (type === 'industry-lab') {
      if (item.id?.startsWith('industry-')) return { label: 'Template', color: 'bg-blue-500' };
    }
    if (item.isMasterTemplate) return { label: 'Template', color: 'bg-amber-500' };
    return null;
  };
  
  const statusBadge = getStatusBadge();

  // Handle file selection - opens crop dialog
  const handleFileSelect = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Error', { description: 'Please select an image file' });
      return;
    }
    
    // Validate file size (max 10MB for pre-crop)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Error', { description: 'Image must be less than 10MB' });
      return;
    }
    
    // Create preview URL and open crop dialog
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setSelectedFile(file);
      setShowCropDialog(true);
      // Reset crop to default 16:9
      setCrop({ unit: '%', width: 100, aspect: 16 / 9 });
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle image load in crop dialog - auto-complete the initial crop
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    // Calculate the maximum crop area with 16:9 aspect ratio
    const aspect = 16 / 9;
    let cropWidth = width;
    let cropHeight = width / aspect;
    
    if (cropHeight > height) {
      cropHeight = height;
      cropWidth = height * aspect;
    }
    
    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;
    
    // Set initial completed crop in pixels
    setCompletedCrop({
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight
    });
    
    setCrop({
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight,
      aspect: 16 / 9
    });
  }, []);

  // Generate cropped image blob
  const getCroppedImg = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!imgRef.current) {
        reject(new Error('Image not loaded'));
        return;
      }
      
      if (!completedCrop || completedCrop.width <= 0 || completedCrop.height <= 0) {
        reject(new Error('Please adjust the crop area'));
        return;
      }

      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      // Set canvas to desired output size (max 1920x1080 for 16:9)
      const maxWidth = 1920;
      const maxHeight = 1080;
      
      const cropWidth = completedCrop.width * scaleX;
      const cropHeight = completedCrop.height * scaleY;
      
      // Ensure we have valid dimensions
      if (cropWidth <= 0 || cropHeight <= 0 || isNaN(cropWidth) || isNaN(cropHeight)) {
        reject(new Error('Invalid crop dimensions'));
        return;
      }
      
      let outputWidth = Math.round(cropWidth);
      let outputHeight = Math.round(cropHeight);
      
      if (outputWidth > maxWidth) {
        const ratio = maxWidth / outputWidth;
        outputWidth = maxWidth;
        outputHeight = Math.round(outputHeight * ratio);
      }
      if (outputHeight > maxHeight) {
        const ratio = maxHeight / outputHeight;
        outputHeight = maxHeight;
        outputWidth = Math.round(outputWidth * ratio);
      }
      
      // Ensure minimum dimensions
      outputWidth = Math.max(1, outputWidth);
      outputHeight = Math.max(1, outputHeight);
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        cropWidth,
        cropHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create image. Please try a different image.'));
          }
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop]);

  // Upload cropped image
  const handleCropAndUpload = async () => {
    if (!completedCrop) {
      toast.error('Error', { description: 'Please select a crop area' });
      return;
    }
    
    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg();
      
      // Create form data with cropped image
      const formData = new FormData();
      formData.append('file', croppedBlob, selectedFile?.name || 'thumbnail.jpg');
      formData.append('type', 'thumbnail');
      
      // Don't set Content-Type header - let browser set it with boundary
      const uploadResponse = await api.post(`/organizations/${orgId}/upload`, formData);
      
      const thumbnailUrl = uploadResponse.data?.url || uploadResponse.data?.fileUrl;
      
      if (thumbnailUrl && onImageUpload) {
        await onImageUpload(item.id, thumbnailUrl);
        toast.success('Success', { description: 'Thumbnail updated!' });
        setImageError(false);
      }
      
      setShowCropDialog(false);
      setImageSrc(null);
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error', { description: error.message || 'Failed to upload image' });
    } finally {
      setUploading(false);
    }
  };
  
  // Get metadata items
  const getMetadata = () => {
    const items = [];
    
    if (item.domain) {
      items.push({ icon: BookOpen, text: item.domain });
    }
    if (item.duration) {
      items.push({ icon: Clock, text: item.duration });
    }
    if (item.topicsCount || item.lessonsCount) {
      items.push({ icon: Layers, text: `${item.topicsCount || item.lessonsCount || 0}` });
    }
    if (item.enrolledCount !== undefined) {
      items.push({ icon: Users, text: `${item.enrolledCount}` });
    }
    
    return items;
  };
  
  const metadata = getMetadata();
  
  // Get type-specific icon
  const TypeIcon = type === 'lab' ? FlaskConical : type === 'industry-lab' ? Building2 : BookOpen;

  // PRD v2.2 — treat any card that's in the "published" lifecycle stage
  // OR has its legacy `isPublished` boolean set as a published card. This
  // catches both new-style courses (lifecycleStage='published',
  // isPublished=false because v2.2 retired the boolean for templates)
  // and legacy ones, so the compact card chrome applies uniformly.
  const isPublishedCard = item.isPublished === true || item.lifecycleStage === 'published';
  
  return (
    <>
      <Card
        className={`group overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-xl hover:-translate-y-1 bg-white flex flex-col h-full ${
          isSelected ? 'ring-2 ring-primary shadow-lg' : ''
        } ${className}`}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={testId}
      >
        {/* Image Container with Padding — compact thumbnail for published cards */}
        <div className={isPublishedCard ? 'p-2 pb-0' : 'p-3 pb-0'}>
          <div className={`relative ${isPublishedCard ? 'aspect-[16/7]' : 'aspect-video'} rounded-lg overflow-hidden bg-gray-100`}>
            {hasCustomImage ? (
              <>
                <img
                  src={optimizeImageUrl(item.thumbnailUrl)}
                  alt={item.title}
                  width={516}
                  height={290}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  onError={() => setImageError(true)}
                />
                {/* Short Title Overlay on custom image */}
                {showShortTitleOverlay && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="text-center px-3">
                      <h2 className="text-white font-semibold text-sm sm:text-base leading-tight drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                        {shortTitle}
                      </h2>
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Gradient placeholder with icon - fills entire area
              <div className={`w-full h-full bg-gradient-to-br ${style.gradient} flex items-center justify-center relative`}>
                <span className="text-7xl opacity-40 select-none">{style.icon}</span>
                
                {/* Upload Overlay - ONLY on gradient placeholder, NOT on custom images */}
                {showUploadButton && isHovered && (
                  <div 
                    className="absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="sm"
                      className="bg-white text-gray-800 hover:bg-gray-100 shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }}
                      disabled={uploading}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Add Image
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Top Badges Row - Only for Labs, not status */}
            <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
              {/* Type Badge for Labs */}
              {type !== 'course' && (
                <Badge variant="secondary" className="bg-white/95 text-gray-800 text-[10px] shadow-md flex items-center gap-1 ml-auto">
                  <TypeIcon className="w-3 h-3" />
                  {type === 'industry-lab' ? 'Industry' : 'AI Lab'}
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        {/* Content Section */}
        <div className="p-3 pt-2 flex-1 flex flex-col">
          {/* PRD v2.2 — Published course cards drop the redundant
              "Published" tag, description block, and difficulty badge.
              Cohort fork cards already convey lifecycle status via the
              footer (Live / Pre-launch / Paused) and published templates
              don't need to repeat the label that already filters the tab. */}
          {/* Status Badge - below thumbnail for courses */}
          {statusBadge && !isPublishedCard && (
            <div className="mb-1.5">
              <Badge className={`${statusBadge.color} text-white text-[10px]`}>
                {statusBadge.label}
              </Badge>
            </div>
          )}
          
          {/* Title - 3 lines minimum visible */}
          <h3 className={`font-semibold text-sm leading-tight mb-1 group-hover:text-primary transition-colors ${isPublishedCard ? 'line-clamp-2 min-h-[2.5rem]' : 'line-clamp-3 min-h-[3.75rem]'}`}>
            {item.title}
          </h3>
          
          {/* Category badge for labs */}
          {item.category && (
            <Badge variant="secondary" className="text-[10px] mb-2 bg-blue-50 text-blue-700 border-blue-200">
              {item.category}
            </Badge>
          )}
          
          {/* AI Categories for labs */}
          {item.aiCategories?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {item.aiCategories.map((cat, idx) => (
                <Badge key={idx} variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200 capitalize">
                  {cat}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Description with tooltip - hide for industry-lab type AND published courses */}
          {item.description && type !== 'industry-lab' && !isPublishedCard && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-[11px] text-gray-500 line-clamp-2 mb-2 cursor-help">
                    {item.description}
                  </div>
                </TooltipTrigger>
                <TooltipContent 
                  side="bottom" 
                  sideOffset={5}
                  className="max-w-[280px] text-xs p-3 bg-gray-900 text-white z-50"
                >
                  <p>{item.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* Metadata Row — hidden for published cards (Domain · Duration
              is noise once shipped). */}
          {metadata.length > 0 && !isPublishedCard && (
            <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-2">
              {metadata.map((meta, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-gray-300">•</span>}
                  <span className="flex items-center gap-1">
                    <meta.icon className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{meta.text}</span>
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
          
          {/* Creator/Instructor */}
          {item.creatorName && (
            <div className="text-[11px] text-gray-500 mb-2 truncate">
              By <span className="font-medium text-gray-700">{item.creatorName}</span>
            </div>
          )}

          {/* Last edited — surfaced on every grid card so authors can spot
              recently-touched items at a glance (matches the default sort). */}
          {item.updatedAt && formatEditedShort(item.updatedAt) && (
            <div
              className="text-[10.5px] text-gray-400 mb-2 flex items-center gap-1"
              data-testid={item.id ? `card-updated-${item.id}` : undefined}
            >
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">Edited {formatEditedShort(item.updatedAt)}</span>
            </div>
          )}
          
          {/* Bottom Row: Difficulty Badge — hidden for published cards
              (Beginner/Intermediate/Advanced is meta noise once a course
              is shipped and the user is filtering by lifecycle anyway).
              For published cards with no master-template flag we drop the
              entire row including the border so the footer hugs tightly. */}
          {(!isPublishedCard || item.isMasterTemplate) && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-auto">
            {item.difficulty && !isPublishedCard ? (
              <Badge 
                variant="outline" 
                className={`text-[10px] px-2 py-0 ${
                  item.difficulty === 'Beginner' ? 'border-green-300 text-green-700 bg-green-50' :
                  item.difficulty === 'Intermediate' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                  'border-red-300 text-red-700 bg-red-50'
                }`}
              >
                {item.difficulty}
              </Badge>
            ) : (
              <div />
            )}
            
            {/* Master Template indicator */}
            {item.isMasterTemplate && (
              <Badge className="bg-amber-100 text-amber-700 text-[10px] flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Template
              </Badge>
            )}
          </div>
          )}
        </div>

        {/* PRD v2.2 / S2.6 — fork cohort-info footer. Renders integrated
            with the card chrome (not floating below) so the visual
            envelope of every grid cell stays consistent. Only appears
            when the item carries a cohortInfo payload (i.e. cohort forks). */}
        {item.cohortInfo && (
          <div
            className="px-3 py-2 bg-indigo-50 border-t border-indigo-100 text-[11px] space-y-1"
            data-testid={item.id ? `card-cohort-info-${item.id}` : undefined}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-indigo-600 shrink-0">🚀</span>
              <span
                className="font-semibold text-indigo-800 truncate flex-1"
                title={item.cohortInfo.name}
              >
                {item.cohortInfo.name}
              </span>
              <span
                className={
                  'shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ' +
                  (item.cohortInfo.status === 'live'
                    ? 'bg-emerald-100 text-emerald-700'
                    : item.cohortInfo.status === 'paused'
                      ? 'bg-slate-200 text-slate-600'
                      : 'bg-amber-100 text-amber-700')
                }
              >
                {item.cohortInfo.status === 'live'
                  ? 'Live'
                  : item.cohortInfo.status === 'paused'
                    ? 'Paused'
                    : 'Pre-launch'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-slate-600 text-[10.5px] flex-wrap">
              <span className="inline-flex items-center gap-1" title="Active members">
                👥 {item.cohortInfo.memberCount} {item.cohortInfo.memberCount === 1 ? 'member' : 'members'}
              </span>
              {item.cohortInfo.startDate && (
                <span className="inline-flex items-center gap-1" title="Cohort schedule">
                  📅 {new Date(item.cohortInfo.startDate).toLocaleDateString()}
                  {item.cohortInfo.endDate && ` → ${new Date(item.cohortInfo.endDate).toLocaleDateString()}`}
                </span>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Image Crop Dialog */}
      <Dialog open={showCropDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCropDialog(false);
          setImageSrc(null);
          setSelectedFile(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crop Image</DialogTitle>
            <DialogDescription>
              Adjust the crop area to select the 16:9 portion of your image that will be displayed as the thumbnail.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Adjust the crop area to select the 16:9 portion of your image that will be displayed as the thumbnail.
            </p>
            
            {imageSrc && (
              <div className="flex justify-center bg-gray-100 rounded-lg p-4 max-h-[400px] overflow-auto">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={16 / 9}
                  className="max-w-full"
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    style={{ maxHeight: '350px', maxWidth: '100%' }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
            )}
            
            <p className="text-xs text-gray-400">
              Tip: Drag the corners to resize, or drag the crop area to reposition. The final image will be optimized for web display.
            </p>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCropDialog(false);
                setImageSrc(null);
                setSelectedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCropAndUpload}
              disabled={uploading || !completedCrop}
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Uploading...
                </span>
              ) : (
                'Crop & Upload'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageCard;